import clsx from "clsx"
import React, { createRef, useCallback, useEffect, useMemo, useState } from "react"
import { CircularProgress, Dialog, Grid, makeStyles } from "@material-ui/core"
import { Localized } from "@fluent/react"
import Screen from "shared/Components/Skeleton/Screen"
import { StandardTopbar, StandardTopbarTitle } from "shared/Components/Skeleton/TopBar"
import SlideUpTransition from "shared/Components/Dialog/SlideUpTransition"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal"
import { HeaderSection, ReceiptContainer } from "../Components/ReceiptBlocks"
import { PurchaseDetailsSection } from "../Components/PurchaseDetailsSection"
import { DeliveryDetailsSection } from "../Components/DeliveryDetailsSection"
import { OrderDetailsSection } from "../Components/OrderDetailsSection"
import {
    MEETING_DELIVERY_MOCK,
    MEETING_ORDERS_CUSTOMER_MOCK,
    MEETING_ORDERS_KITCHEN_MOCK,
    MEETING_ORDERS_PAYMENT
} from "../../../Static/MeetingPlanning/deliveryForm"
import { DateTime } from "luxon"
import { BuyerParty, PaymentMethodType, WebshopType } from "../../../Types/appTypes"
import { COMPANY_PAYMENT_METHOD } from "../../Transaction/paymentLib"
import {
    IMeetingServiceOrder,
    IMeetingServiceOrderLine,
    MeetingServiceFlatOrderLineGroup
} from "../../../Types/MeetingPlanning/meetingServiceOrders"
import MeetingServiceActions from "../Components/MeetingService/MeetingServiceActions"
import { useQuery } from "../../Query/useQuery"
import { CurrentEnvironment } from "../../Environment/envTypes"
import { isEmpty } from "lodash-es"
import { useDispatch, useSelector } from "react-redux"
import {
    DeliveryPayloadType,
    DeliveryType,
    IEditMeetingOrderData
} from "../../../Types/MeetingPlanning/deliveryForm"
import { editMeetingOrder } from "../../MeetingPlanning/Redux/EditOrder/MeetingPlanningEditOrderActions"
import { selectUserCanteenId } from "../../User/userSelectors"
import { useAppNavigation } from "../../../hooks/useAppNavigation"
import { getMetingServiceDetails } from "../orderDetailsAPI"
import { useDateTime } from "../../Localization/useDateTime"
import { roundToNearest } from "../../../Helpers/MathHelper"
import { createEnumChecker } from "shared/Helpers/enums"

type DetailedReceiptModalProps = Readonly<{
    token: string
    environment: CurrentEnvironment
    order: IMeetingServiceOrder
    open: boolean
    onClose: () => void
}>

const useStyles = makeStyles((theme) => ({
    fullScreenHeight: {
        height: '100vh'
    },
    scrollShadowBottom: {
        "&::after": {
            zIndex: 1,
            position: "fixed",
            content: '"-"',
            bottom: "-15px",
            left: "0",
            width: "100%",
            boxShadow: "0 -5px 25px rgb(0 0 0 / 18%)"
        }
    },
    edge: {
        height: "10px"
    }
}))

type DetailedReceiptProps = Readonly<{
    token: string
    environment: CurrentEnvironment
    order: IMeetingServiceOrder
    classes: ReturnType<typeof useStyles>
    setIsAtBottom: (atBottom: boolean) => void
    onClose: () => void
}>

type GroupedOrderLines = Record<BuyerParty, Record<PaymentMethodType, IMeetingServiceOrderLine[]>>

const isBuyerParty = createEnumChecker(BuyerParty)

function getGroupedOrderLines(loadedOrder: IMeetingServiceOrder) {
    // Today's orders does not contain payment methods for order
    if (!loadedOrder) { return undefined }

    const orderLines = loadedOrder.orderLines
    if (!orderLines) { return undefined }

    return orderLines.reduce((acc, line) => {
        if (line.buyerParty && isBuyerParty(line.buyerParty)) {
            const paymentMethod = COMPANY_PAYMENT_METHOD

            let buyerPartyGroups = acc[line.buyerParty]
            if (!buyerPartyGroups) {
                buyerPartyGroups = {} as Record<PaymentMethodType, IMeetingServiceOrderLine[]>
                acc[line.buyerParty] = buyerPartyGroups
            }

            let paymentMethodLines = buyerPartyGroups[paymentMethod]
            if (!paymentMethodLines) {
                paymentMethodLines = []
                buyerPartyGroups[paymentMethod] = paymentMethodLines
            }

            paymentMethodLines.push(line)
        }
        return acc
    }, {} as GroupedOrderLines)
}

function getIncludedVAT(totalPrice: number) {
    const dkVatRate = 0.25
    const vatMultiplier = 1 + dkVatRate
    const priceWithoutVat = totalPrice / vatMultiplier
    return totalPrice - priceWithoutVat
}

function flattenGroupedOrderLines(orderLines: GroupedOrderLines | undefined) {
    if (!orderLines) { return undefined }

    return Object.entries(orderLines).reduce((allGroups, [buyerParty, group]) => {
        return Object.entries(group).reduce((bpGroups, [paymentMethod, ol]) => {
            const totalPrice = ol.reduce((sum, line) => sum + (line.price?.amount ?? 0), 0)
            const includedVAT = getIncludedVAT(totalPrice)

            const flatGroup: MeetingServiceFlatOrderLineGroup = {
                buyerParty: buyerParty as BuyerParty,
                paymentMethod: paymentMethod as PaymentMethodType,
                orderLines: ol,
                totalPrice,
                includedVAT,
            }
            bpGroups.push(flatGroup)
            return bpGroups
        }, allGroups)
    }, [] as MeetingServiceFlatOrderLineGroup[])
}

function DetailedMeetingReceipt({ token, environment, order, classes, setIsAtBottom, onClose }: DetailedReceiptProps) {
    const bottomEdge = createRef<HTMLDivElement>()
    const [loading, setLoading] = useState(false)
    const dateTimeFactory = useDateTime()

    const { goToWebshop } = useAppNavigation()

    const userCanteenId = useSelector(selectUserCanteenId)
    const dispatch = useDispatch()

    const { response: orderDetail } = useQuery(
        () => {
            setLoading(true)
            return getMetingServiceDetails(token, environment, order.id).finally(() => setLoading(false))
        },
        [token, environment, order.id],
    )

    const currentOrder = useMemo(() => {
        if (!orderDetail.loading &&
            !orderDetail.failed &&
            orderDetail.response &&
            !isEmpty(orderDetail.response.data)) {
            return orderDetail.response.data.orders.find((o) => o.id === order.id)
        }

        return null
    }, [orderDetail.loading])

    const displayName = useMemo(() => currentOrder?.meetingDetails?.meetingName ?? `${currentOrder?.id}`, [currentOrder?.id, currentOrder?.meetingDetails?.meetingName])

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            for (const intersection of entries) {
                if (intersection.target.id === "bottomEdge") {
                    setIsAtBottom(intersection.isIntersecting)
                }
            }
        }, {
            threshold: 1.0
        })

        if (bottomEdge.current) { observer.observe(bottomEdge.current) }

        return () => observer.disconnect()
    }, [])

    const groupedOrderLines = useMemo(
        () => flattenGroupedOrderLines(getGroupedOrderLines(order)),
        []
    )

    const topbar = (
        <StandardTopbar
            middleElement={
                <Localized id="receipt-title">
                    <StandardTopbarTitle>Kvittering</StandardTopbarTitle>
                </Localized>
            }
            onClose={onClose}
        />
    )

    const editOrder = useCallback(() => {
        if (userCanteenId && currentOrder) {
            // TODO: This is mock data for edit order - will change in future
            const orderDetails: IEditMeetingOrderData = {
                orderId: currentOrder.id,
                orderData: {
                    deliveryDate: currentOrder.deliveryDetails.time.split(' ')[0],
                    deliveryTime: dateTimeFactory.toIsoTime(dateTimeFactory.fromFNDateTime(currentOrder.deliveryDetails.time)),
                    meetingName: currentOrder.meetingDetails?.meetingName || '',
                    seats: currentOrder.deliveryDetails.sittings,
                    deliveryType: currentOrder.deliveryDetails.type,
                    deliveryLocation: '1',
                    hasOtherDeliveryLocation: false,
                    otherDeliveryLocation: '',
                    otherMessage: currentOrder.orderNote,
                    isConfirmed: false,
                    deadlineMessage: '',
                    deadlineMerchantMessage: '',
                    extraServices: [],
                    billReceiver: [{label: 'FacilityNet ApS', value: '1'}],
                    costCenters: [{ id: 1, label: '', value: '0010' }]
                },
                products: [{
                    id: 375,
                    date: DateTime.fromJSDate(new Date(currentOrder.deliveryDetails.time.split(' ')[0]))
                }],
                isEditOrder: true
            }
            goToWebshop(userCanteenId, WebshopType.MEETING_BOOKING)
            dispatch(editMeetingOrder(orderDetails))
        }
    }, [goToWebshop, userCanteenId, currentOrder])

    return loading ? <Grid container item alignItems="center" justifyContent="center" className={classes.fullScreenHeight}>
            <CircularProgress variant="indeterminate" />
        </Grid> : <Screen
            name="orderReceiptDetailed"
            alternativeHeaderElement={topbar}
            containerPadding={0}
            fitPage={true}
        >
            {currentOrder && (<>
                <ReceiptContainer>
                    <HeaderSection>{displayName}</HeaderSection>

                    <PurchaseDetailsSection
                        paymentDetails={MEETING_ORDERS_PAYMENT}
                        kitchen={MEETING_ORDERS_KITCHEN_MOCK}
                        customer={MEETING_ORDERS_CUSTOMER_MOCK}
                        timeOfPurchase={dateTimeFactory.fromFNDateTime(currentOrder.deliveryDetails.time)}
                        orderId={`${order.id}`}
                        shopChannel={'GoPay app (web)'}
                    />

                    <MeetingServiceActions
                        orderId={currentOrder.id}
                        orderUid={currentOrder.uid}
                        orderAction={currentOrder.orderAction}
                        handleEditOrder={editOrder}
                    />

                    <DeliveryDetailsSection
                        orderType={currentOrder.orderType}
                        delivery={MEETING_DELIVERY_MOCK}
                        deliveryComment={{
                            present: true,
                            text: currentOrder.orderNote
                        }}
                    />

                    {groupedOrderLines ? groupedOrderLines.map((group, index, array) => (
                        <OrderDetailsSection key={index} orderDetails={group} final={index === array.length - 1} />
                    )) : null}
                </ReceiptContainer>
                <div ref={bottomEdge} id="bottomEdge" className={classes.edge} />
            </>)}
        </Screen>
}

export default function DetailedMeetingReceiptModal({ token, environment, order, open, onClose }: DetailedReceiptModalProps) {
    const classes = useStyles()
    const [isAtBottom, setIsAtBottom] = useState(false)

    return (
        <Dialog open={open} TransitionComponent={SlideUpTransition} fullScreen={true} scroll="paper" PaperProps={{ elevation: 0 }} classes={{ paperScrollPaper: clsx({ [classes.scrollShadowBottom]: !isAtBottom}) }}>
            <ErrorHandlerModal close={onClose}>
                <DetailedMeetingReceipt token={token} environment={environment} order={order} classes={classes} setIsAtBottom={setIsAtBottom} onClose={onClose} />
            </ErrorHandlerModal>
        </Dialog>
    )
}
